/**
 * usePermission Hook
 *
 * Role-based permission checking with database-backed customization.
 * Supports both legacy role-based checks and new granular permissions.
 *
 * In academy mode, permissions are loaded from the database and can be
 * customized per academy. In personal mode, default permissions apply.
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserRole } from '~/types/database';
import { useUser } from './useUser';
import { useWorkspaceStore } from '~/core/store/useWorkspaceStore';
import {
  fetchAcademyPermissions,
  fetchTeacherPermissions,
  ALL_PERMISSIONS,
} from '~/services/workspace/permissionService';

// ============================================================================
// TYPES
// ============================================================================

export interface UsePermissionReturn {
  /** User has OWNER role */
  isOwner: boolean;
  /** User has ADMIN role (includes owner) */
  isAdmin: boolean;
  /** User has TEACHER role */
  isTeacher: boolean;
  /** User's role string */
  role: UserRole | null;
  /** User is authenticated */
  isAuthenticated: boolean;
  /** Loading state (while fetching user/profile or permissions) */
  isLoading: boolean;
  /** Check if user has specific role */
  hasRole: (role: UserRole) => boolean;
  /** Check if user can perform admin actions */
  canAdmin: () => boolean;

  // ---- NEW: Granular permission check ----
  /** Check if user has a specific permission (e.g., "session.approve") */
  hasPermission: (permission: string) => boolean;
  /** Permissions loading state */
  isPermissionsLoading: boolean;

  // ---- LEGACY: Keep for backward compatibility ----
  /** @deprecated Use hasPermission('session.create') or hasPermission('session.edit_own') */
  canWriteSessions: () => boolean;
  /** @deprecated Use hasPermission('session.approve') */
  canApproveRequests: () => boolean;
  /** @deprecated Use hasPermission('student.edit') */
  canManageStudents: () => boolean;
  /** @deprecated Use hasPermission('session.create') */
  canSubmitRequests: () => boolean;
  /** @deprecated Use hasPermission('teacher.invite') */
  canInviteStaff: () => boolean;
}

// ============================================================================
// FALLBACK PERMISSIONS (used when DB not loaded)
// ============================================================================

/**
 * Default permissions by role (fallback when DB is not available)
 * These match the default_permissions table in the database
 */
const FALLBACK_PERMISSIONS: Record<UserRole, Record<string, boolean>> = {
  OWNER: Object.fromEntries(ALL_PERMISSIONS.map(p => [p, true])),
  ADMIN: {
    'session.create': true,
    'session.view': true,
    'session.edit_own': true,
    'session.edit_all': true,
    'session.delete_own': true,
    'session.delete_all': true,
    'session.approve': true,
    'student.create': true,
    'student.view': true,
    'student.edit': true,
    'student.delete': true,
    'teacher.view': true,
    'teacher.invite': true,
    'teacher.edit': true,
    'teacher.delete': true,
    'course.create': true,
    'course.view': true,
    'course.edit': true,
    'course.delete': true,
    'log.create': true,
    'log.view_own': true,
    'log.view_all': true,
    'log.edit_own': true,
    'log.delete_own': true,
    'report.view': true,
    'report.export': true,
    'settings.view': true,
    'settings.manage_academy': true,
    'settings.manage_permissions': false,
    'materials.restore': true,
  },
  MANAGER: {
    'session.create': true,
    'session.view': true,
    'session.edit_own': true,
    'session.edit_all': true,
    'session.delete_own': true,
    'session.delete_all': false,
    'session.approve': true,
    'student.create': true,
    'student.view': true,
    'student.edit': true,
    'student.delete': false,
    'teacher.view': true,
    'teacher.invite': false,
    'teacher.edit': false,
    'teacher.delete': false,
    'course.create': true,
    'course.view': true,
    'course.edit': true,
    'course.delete': false,
    'log.create': true,
    'log.view_own': true,
    'log.view_all': true,
    'log.edit_own': true,
    'log.delete_own': true,
    'report.view': true,
    'report.export': false,
    'settings.view': true,
    'settings.manage_academy': false,
    'settings.manage_permissions': false,
    'materials.restore': false,
  },
  TEACHER: {
    'session.create': true,
    'session.view': true,
    'session.edit_own': true,
    'session.edit_all': false,
    'session.delete_own': true,
    'session.delete_all': false,
    'session.approve': false,
    'student.create': false,
    'student.view': true,
    'student.edit': false,
    'student.delete': false,
    'teacher.view': true,
    'teacher.invite': false,
    'teacher.edit': false,
    'teacher.delete': false,
    'course.create': true,
    'course.view': true,
    'course.edit': true,
    'course.delete': false,
    'log.create': true,
    'log.view_own': true,
    'log.view_all': false,
    'log.edit_own': true,
    'log.delete_own': true,
    'report.view': false,
    'report.export': false,
    'settings.view': false,
    'settings.manage_academy': false,
    'settings.manage_permissions': false,
    'materials.restore': false,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if role is admin (includes owner and manager)
 */
function checkIsAdmin(role: UserRole | null): boolean {
  return role === 'ADMIN' || role === 'OWNER' || role === 'MANAGER';
}

/**
 * Check if role is owner
 */
function checkIsOwner(role: UserRole | null): boolean {
  return role === 'OWNER';
}

/**
 * Check if role is teacher
 */
function checkIsTeacher(role: UserRole | null): boolean {
  return role === 'TEACHER';
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * usePermission - Role-based permission hook with database support
 *
 * @example
 * ```tsx
 * function ApproveButton() {
 *   const { hasPermission, isPermissionsLoading } = usePermission();
 *
 *   if (isPermissionsLoading) return <Spinner />;
 *   if (!hasPermission('session.approve')) return null;
 *
 *   return <button>승인</button>;
 * }
 * ```
 */
export function usePermission(): UsePermissionReturn {
  const { profile, isAuthenticated, isLoading: isUserLoading } = useUser();
  const { active } = useWorkspaceStore();


  // Extract role from profile
  const role = useMemo(() => profile?.role ?? null, [profile]);

  // Get workspace role (may differ from profile role in multi-academy setup)
  const workspaceRole = useMemo((): UserRole | null => {
    if (active.type === 'academy') {
      // Convert lowercase role to uppercase UserRole
      const roleMap: Record<string, UserRole> = {
        owner: 'OWNER',
        admin: 'ADMIN',
        manager: 'MANAGER',
        teacher: 'TEACHER',
      };
      return roleMap[active.role] || role;
    }
    return role;
  }, [active, role]);

  // Memoize role checks (using workspace role)
  const isOwner = useMemo(() => checkIsOwner(workspaceRole), [workspaceRole]);
  const isAdmin = useMemo(() => checkIsAdmin(workspaceRole), [workspaceRole]);
  const isTeacher = useMemo(() => checkIsTeacher(workspaceRole), [workspaceRole]);

  // 권한 로드 — ISSUE_167: 권한 = 마스터데이터 → RQ 단순캐시(서버 권위). syncEnabled 게이트 없음.
  // (구 readThroughCache는 cache-utils의 syncEnabled 게이트 경유 → 미결제 + IndexedDB 캐시 없는
  //  신규 로그인 시 비-OWNER 권한 미로드 버그. RQ로 이전해 항상 서버 조회 + RQ persist 캐시.)
  const permsQuery = useQuery<Record<string, boolean>>({
    queryKey: [
      'permissions',
      active.type === 'academy' ? active.id : null,
      profile?.id ?? null,
      workspaceRole,
    ],
    // academy + 비-OWNER만 DB 권한 필요(OWNER/개인모드는 hasPermission이 단락 처리)
    enabled: active.type === 'academy' && !!workspaceRole && workspaceRole !== 'OWNER',
    staleTime: 10 * 60 * 1000, // 권한은 자주 안 변함
    queryFn: async () => {
      const academyId = active.type === 'academy' ? active.id : '';
      const profileId = profile?.id;
      // role-based + personal(teacher) 권한 둘 다 조회 후 personal 우선 머지
      const [roleResult, personalResult] = await Promise.all([
        fetchAcademyPermissions(academyId, workspaceRole!),
        profileId ? fetchTeacherPermissions(academyId, profileId) : Promise.resolve({ success: false }),
      ]);

      // ISSUE_073: role fetch 실패 시 throw → RQ가 직전 성공 데이터 유지(빈 {}로 덮어쓰지 않음)
      if (!roleResult.success) {
        throw new Error('Permission fetch failed');
      }

      const permObj: Record<string, boolean> = {};
      if (roleResult.data) {
        for (const item of roleResult.data) {
          permObj[item.permission] = item.granted;
        }
      }
      // teacher_permissions가 role 기본값을 덮어씀
      if (personalResult.success && 'data' in personalResult && Array.isArray(personalResult.data)) {
        for (const item of personalResult.data as { permission: string; granted: boolean }[]) {
          permObj[item.permission] = item.granted;
        }
      }
      return permObj;
    },
  });

  // RQ data → permissions Map. OWNER/개인모드는 query disabled → null → hasPermission이 단락 처리.
  const permissions = useMemo(
    () => (permsQuery.data ? new Map(Object.entries(permsQuery.data)) : null),
    [permsQuery.data],
  );
  const isPermissionsLoading = permsQuery.isPending && permsQuery.fetchStatus !== 'idle';

  // ---- NEW: hasPermission function ----
  const hasPermission = useCallback(
    (permission: string): boolean => {
      // Not authenticated or no role
      if (!isAuthenticated || !workspaceRole) {
        return false;
      }

      // OWNER always has all permissions
      if (workspaceRole === 'OWNER') {
        return true;
      }

      // Check loaded permissions first
      if (permissions) {
        const granted = permissions.get(permission);
        if (granted !== undefined) {
          return granted;
        }
      }

      // Fallback to defaults
      const fallback = FALLBACK_PERMISSIONS[workspaceRole];
      return fallback?.[permission] ?? false;
    },
    [isAuthenticated, workspaceRole, active.type, permissions]
  );

  // ---- LEGACY: Backward-compatible permission functions ----
  const hasRole = useCallback(
    (checkRole: UserRole): boolean => workspaceRole === checkRole,
    [workspaceRole]
  );

  const canAdmin = useCallback(
    (): boolean => isAdmin,
    [isAdmin]
  );

  const canWriteSessions = useCallback(
    (): boolean => hasPermission('session.create') || hasPermission('session.edit_own'),
    [hasPermission]
  );

  const canApproveRequests = useCallback(
    (): boolean => hasPermission('session.approve'),
    [hasPermission]
  );

  const canManageStudents = useCallback(
    (): boolean => hasPermission('student.edit'),
    [hasPermission]
  );

  const canSubmitRequests = useCallback(
    (): boolean => hasPermission('session.create'),
    [hasPermission]
  );

  const canInviteStaff = useCallback(
    (): boolean => hasPermission('teacher.invite'),
    [hasPermission]
  );

  return {
    isOwner,
    isAdmin,
    isTeacher,
    role: workspaceRole,
    isAuthenticated,
    isLoading: isUserLoading,
    hasRole,
    canAdmin,
    // New
    hasPermission,
    isPermissionsLoading,
    // Legacy
    canWriteSessions,
    canApproveRequests,
    canManageStudents,
    canSubmitRequests,
    canInviteStaff,
  };
}

/**
 * useRequireAdmin - Hook that throws if user is not admin
 */
export function useRequireAdmin(): { isLoading: boolean } {
  const { isAdmin, isLoading, isAuthenticated } = usePermission();

  if (!isLoading && !isAuthenticated) {
    throw new Error('Authentication required');
  }

  if (!isLoading && !isAdmin) {
    throw new Error('Admin access required');
  }

  return { isLoading };
}

/**
 * useRequireAuth - Hook that throws if user is not authenticated
 */
export function useRequireAuth(): { isLoading: boolean } {
  const { isAuthenticated, isLoading } = usePermission();

  if (!isLoading && !isAuthenticated) {
    throw new Error('Authentication required');
  }

  return { isLoading };
}

/**
 * useRequirePermission - Hook that throws if user doesn't have permission
 */
export function useRequirePermission(permission: string): { isLoading: boolean } {
  const { hasPermission, isLoading, isPermissionsLoading, isAuthenticated } = usePermission();

  if (!isLoading && !isAuthenticated) {
    throw new Error('Authentication required');
  }

  if (!isLoading && !isPermissionsLoading && !hasPermission(permission)) {
    throw new Error(`Permission required: ${permission}`);
  }

  return { isLoading: isLoading || isPermissionsLoading };
}

export default usePermission;
